<template>
  <div class="beepgo-login-wrapper" :dir="isRtl ? 'rtl' : 'ltr'">
    <!-- ==================== LEFT BRAND PANEL ==================== -->
    <div class="left-brand-panel">
      <!-- Top Brand Content -->
      <div class="brand-top-content">
        <div class="brand-logo-container">
          <img
            :src="require('@/assets/images/login/beepgo-logo.png')"
            alt="BeepGo"
            class="beepgo-brand-logo"
          />
        </div>

        <h1 class="brand-headline">{{ welcomeHeadline }}</h1>
        <p class="brand-copy">{{ welcomeSubtitle }}</p>
      </div>

      <!-- Bottom Transportation Visual Atmosphere -->
      <div class="brand-transport-scene">
        <img
          :src="require('@/assets/images/login/transport.png')"
          alt="BeepGo Transportation"
          class="transport-illustration"
        />
      </div>
    </div>

    <!-- ==================== RIGHT LOGIN AREA ==================== -->
    <div class="right-login-panel">
      <!-- Decorative Leaves Accent (Bottom Corner) -->
      <div class="leaves-accent-container">
        <img
          :src="require('@/assets/images/login/leaves.png')"
          alt=""
          class="leaves-accent-img"
        />
      </div>

      <!-- Main Login Card Container -->
      <div class="login-card-container">
        <vue-element-loading :active="submiting" spinner="bar-fade-scale" color="#082C69" />

        <div class="login-card">
          <!-- Language Selector (Top Corner) -->
          <div class="lang-selector-corner">
            <v-menu offset-y transition="slide-y-transition" rounded="lg">
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  class="lang-pill-btn"
                  v-bind="attrs"
                  v-on="on"
                  :aria-label="$t('Change Language')"
                >
                  <img
                    :src="require(`@/assets/flags/${selectedLang}.png`)"
                    alt=""
                    class="lang-flag"
                  />
                  <span class="lang-code">{{ selectedLang.toUpperCase() }}</span>
                  <svg class="lang-chevron" viewBox="0 0 24 24" width="14" height="14">
                    <path fill="currentColor" d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                  </svg>
                </button>
              </template>
              <v-list dense class="lang-menu-list pa-1">
                <v-list-item
                  v-for="lang in availableLanguagesList"
                  :key="lang.code"
                  @click="changeLanguage(lang.code)"
                  class="lang-menu-item"
                  :class="{ 'active-lang': selectedLang === lang.code }"
                >
                  <img
                    :src="require(`@/assets/flags/${lang.code}.png`)"
                    alt=""
                    class="lang-flag me-2"
                  />
                  <v-list-item-title class="lang-name">{{ lang.name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <!-- Login Portal Door Illustration -->
          <div class="door-icon-wrapper">
            <img
              :src="require('@/assets/images/login/login-door.png')"
              alt="Login"
              class="door-illustration"
            />
          </div>

          <!-- Header Title & Subtitle -->
          <h2 class="card-title">{{ loginTitle }}</h2>
          <p class="card-subtitle">{{ loginSubtitle }}</p>

          <!-- Authentication Error Alert -->
          <v-alert
            v-if="authError"
            type="error"
            dense
            text
            dismissible
            class="auth-error-alert mb-4"
            @input="authError = null"
          >
            {{ authError }}
          </v-alert>

          <!-- Login Form -->
          <v-form
            ref="form"
            v-model="valid"
            lazy-validation
            @submit.prevent="login"
          >
            <!-- Email Field -->
            <div class="form-group mb-4">
              <label class="custom-field-label">{{ $t('Email') }}</label>
              <v-text-field
                v-model="email"
                outlined
                dense
                single-line
                :placeholder="emailPlaceholder"
                :prepend-inner-icon="icons.mdiEmailOutline"
                required
                :rules="emailRules"
                class="custom-input-field"
                autocomplete="email"
              ></v-text-field>
            </div>

            <!-- Password Field -->
            <div class="form-group mb-3">
              <label class="custom-field-label">{{ $t('Password') }}</label>
              <v-text-field
                v-model="password"
                outlined
                dense
                single-line
                :type="isPasswordVisible ? 'text' : 'password'"
                :placeholder="passwordPlaceholder"
                :prepend-inner-icon="icons.mdiLockOutline"
                :append-icon="isPasswordVisible ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
                required
                :rules="passRules"
                class="custom-input-field"
                autocomplete="current-password"
                @click:append="isPasswordVisible = !isPasswordVisible"
              ></v-text-field>
            </div>

            <!-- Options Row: Remember Me & Forgot Password -->
            <div class="d-flex align-center justify-space-between options-row mb-6">
              <v-checkbox
                v-model="rememberMe"
                :label="$t('Remember Me')"
                hide-details
                class="remember-checkbox ma-0 pa-0"
                color="#082C69"
              ></v-checkbox>

              <router-link
                class="forgot-password-link"
                to="/forgot-password"
              >
                {{ forgotPasswordText }}
              </router-link>
            </div>

            <!-- Submit Button -->
            <v-btn
              block
              class="login-submit-btn"
              height="54"
              :loading="submiting"
              :disabled="submiting"
              type="submit"
            >
              <span v-if="isRtl" class="d-inline-flex align-center justify-center">
                <v-icon class="btn-arrow me-2" size="20">{{ icons.mdiArrowRight }}</v-icon>
                <span class="btn-text">{{ $t('Login') }}</span>
              </span>
              <span v-else class="d-inline-flex align-center justify-center">
                <span class="btn-text">{{ $t('Login') }}</span>
                <v-icon class="btn-arrow ms-2" size="20">{{ icons.mdiArrowRight }}</v-icon>
              </span>
            </v-btn>
          </v-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  mdiEyeOutline,
  mdiEyeOffOutline,
  mdiEmailOutline,
  mdiLockOutline,
  mdiArrowRight,
  mdiArrowLeft,
  mdiAlertCircleOutline,
} from '@mdi/js';
import AuthService from "@/services/AuthService";
import VueElementLoading from "vue-element-loading";
import { Keys } from '/src/config.js';

export default {
  name: 'LoginView',
  components: {
    VueElementLoading,
  },
  data() {
    return {
      systemName: Keys.VUE_APP_SYSTEM_NAME,
      email: '',
      password: '',
      isPasswordVisible: false,
      rememberMe: localStorage.getItem('beepgo_remember_me') === 'true',
      selectedLang: 'ar',
      valid: true,
      submiting: false,
      authError: null,
      icons: {
        mdiEyeOutline,
        mdiEyeOffOutline,
        mdiEmailOutline,
        mdiLockOutline,
        mdiArrowRight,
        mdiArrowLeft,
        mdiAlertCircleOutline,
      },
    };
  },
  computed: {
    isRtl() {
      return (this.selectedLang || this.$i18n.locale) === 'ar';
    },
    welcomeHeadline() {
      return this.selectedLang === 'ar' ? 'مرحباً بك في BeepGo' : 'Welcome to BeepGo';
    },
    welcomeSubtitle() {
      return this.selectedLang === 'ar'
        ? 'سجل الدخول لإدارة حسابك\nوالاستمتاع بخدماتنا بسهولة وأمان'
        : 'Sign in to manage transportation operations\nand monitor your services safely and easily';
    },
    loginTitle() {
      return this.selectedLang === 'ar' ? 'تسجيل الدخول' : this.$t('Login');
    },
    loginSubtitle() {
      return this.selectedLang === 'ar' ? 'أدخل بياناتك للمتابعة' : 'Enter your credentials to continue';
    },
    emailPlaceholder() {
      return 'example@beepgo.com';
    },
    passwordPlaceholder() {
      return '••••••••';
    },
    forgotPasswordText() {
      return this.selectedLang === 'ar' ? 'نسيت كلمة المرور؟' : this.$t('Forgot Password?');
    },
    availableLanguagesList() {
      const langNames = {
        ar: 'العربية',
        en: 'English',
        fr: 'Français',
        es: 'Español',
      };
      const langs = this.$availableLanguages || ['ar', 'en', 'fr', 'es'];
      return langs.map(code => ({
        code,
        name: langNames[code] || code.toUpperCase(),
      }));
    },
    emailRules() {
      return [
        v => !!v || this.getError('E-mail is required'),
        v => /.+@.+\..+/.test(v) || this.getError('E-mail must be valid'),
      ];
    },
    passRules() {
      return [
        v => !!v || this.getError('Password is required'),
      ];
    },
  },
  mounted() {
    this.selectedLang = localStorage.getItem("lang") || this.$i18n.locale || 'ar';
    this.$setDocumentLangDir(this.selectedLang);
    if (this.rememberMe) {
      const savedEmail = localStorage.getItem('beepgo_saved_email');
      if (savedEmail) {
        this.email = savedEmail;
      }
    }
  },
  methods: {
    getError(message) {
      return this.$t(message);
    },
    changeLanguage(lang) {
      this.selectedLang = lang;
      this.$i18n.locale = lang;
      this.$setDocumentLangDir(lang);
    },
    validate() {
      return this.$refs.form.validate();
    },
    async login() {
      if (!this.validate()) return;
      const payload = {
        email: this.email,
        password: this.password,
        notify: this.$notify,
      };
      this.authError = null;
      try {
        this.submiting = true;
        const isLoggedIn = await AuthService.login(payload);
        this.submiting = false;
        if(isLoggedIn)
        {
          if (this.rememberMe) {
            localStorage.setItem('beepgo_remember_me', 'true');
            localStorage.setItem('beepgo_saved_email', this.email);
          } else {
            localStorage.removeItem('beepgo_remember_me');
            localStorage.removeItem('beepgo_saved_email');
          }
          const userRole = AuthService.getUserRole();
          const defaultTarget = AuthService.getRoleLandingRoute(userRole);
          let target = this.$router.currentRoute.query.to || defaultTarget;
          // Ensure Role 5, Role 4 and Role 3 never land on the legacy dashboard
          if (userRole === 5 && (target === '/' || target === '/dashboard' || target === '/home')) {
            target = '/customer-service';
          } else if (userRole === 4 && (target === '/' || target === '/dashboard' || target === '/home')) {
            target = '/operations';
          } else if (userRole === 3 && (target === '/' || target === '/dashboard' || target === '/home')) {
            target = '/routes';
          }
          this.$router.push(target);
        }
      } catch (error) {
        console.log(error);
        this.submiting = false;
        this.authError = error?.message || (this.selectedLang === 'ar' ? 'فشل تسجيل الدخول. تحقق من البيانات المدخلة.' : 'Login failed. Please check your credentials.');
      }
    },
  },
};
</script>

<style lang="scss">
/* ==========================================================================
   BeepGo Admin Dashboard — Login Page Premium Styles
   Brand Identity: Navy (#082C69, #061B40) & Gold (#F5A114)
   ========================================================================== */

.beepgo-login-wrapper {
  display: flex;
  flex-direction: row;
  min-height: 100vh;
  width: 100%;
  background-color: #F8FAFD;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  font-family: 'Cairo', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;

  *, *::before, *::after {
    box-sizing: border-box;
  }

  // ==================== LEFT BRAND PANEL ====================
  .left-brand-panel {
    flex: 0 0 48%;
    max-width: 48%;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 56px 64px 0;
    position: relative;
    z-index: 2;
    background: radial-gradient(circle at 15% 15%, rgba(245, 161, 20, 0.05) 0%, transparent 40%),
                radial-gradient(circle at 85% 70%, rgba(8, 44, 105, 0.04) 0%, transparent 50%),
                #F8FAFD;

    .brand-top-content {
      display: flex;
      flex-direction: column;
      z-index: 3;

      .brand-logo-container {
        margin-bottom: 36px;

        .beepgo-brand-logo {
          height: 52px;
          max-width: 240px;
          object-fit: contain;
          display: block;
        }
      }

      .brand-headline {
        font-size: 38px;
        font-weight: 700;
        color: #082C69;
        line-height: 1.35;
        margin-bottom: 16px;
        letter-spacing: -0.5px;
      }

      .brand-copy {
        font-size: 17.5px;
        font-weight: 400;
        color: #5A6A85;
        line-height: 1.7;
        max-width: 460px;
        margin: 0;
        white-space: pre-line;
      }
    }

    .brand-transport-scene {
      position: relative;
      width: 100%;
      margin-top: auto;
      z-index: 1;
      display: flex;
      align-items: flex-end;
      justify-content: flex-start;

      .transport-illustration {
        width: 100%;
        max-width: 580px;
        height: auto;
        display: block;
        object-fit: contain;
        pointer-events: none;
      }
    }
  }

  // ==================== RIGHT LOGIN PANEL ====================
  .right-login-panel {
    flex: 0 0 52%;
    max-width: 52%;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 32px;
    position: relative;
    z-index: 3;

    .leaves-accent-container {
      position: absolute;
      bottom: 0;
      right: 0;
      pointer-events: none;
      z-index: 1;

      .leaves-accent-img {
        height: 230px;
        width: auto;
        display: block;
        opacity: 0.9;
      }
    }

    .login-card-container {
      width: 100%;
      max-width: 530px;
      position: relative;
      z-index: 4;

      .login-card {
        background: #FFFFFF !important;
        border-radius: 24px !important;
        padding: 40px 44px 44px;
        box-shadow: 0 24px 60px -15px rgba(8, 44, 105, 0.08), 0 2px 10px rgba(8, 44, 105, 0.03) !important;
        border: 1px solid rgba(226, 232, 240, 0.85);
        position: relative;
      }
    }
  }

  // ==================== LANGUAGE SELECTOR ====================
  .lang-selector-corner {
    position: absolute;
    top: 24px;
    inset-inline-end: 24px;
    z-index: 10;

    .lang-pill-btn {
      display: inline-flex;
      align-items: center;
      padding: 6px 12px;
      background: #F1F5F9;
      border: 1px solid #E2E8F0;
      border-radius: 20px;
      cursor: pointer;
      outline: none;
      transition: all 0.2s ease;

      &:hover {
        background: #E2E8F0;
        border-color: #CBD5E1;
      }

      .lang-flag {
        width: 20px;
        height: 15px;
        border-radius: 2px;
        object-fit: cover;
      }

      .lang-code {
        font-size: 13px;
        font-weight: 600;
        color: #334155;
        margin: 0 6px;
      }

      .lang-chevron {
        color: #64748B;
        transition: transform 0.2s ease;
      }
    }
  }

  // ==================== CARD HEADER ====================
  .door-icon-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 10px;
    margin-bottom: 18px;

    .door-illustration {
      width: 90px;
      height: auto;
      object-fit: contain;
      filter: drop-shadow(0 4px 12px rgba(8, 44, 105, 0.08));
    }
  }

  .card-title {
    font-size: 28px;
    font-weight: 700;
    color: #082C69;
    text-align: center;
    margin-bottom: 6px;
    letter-spacing: -0.3px;
  }

  .card-subtitle {
    font-size: 15px;
    color: #64748B;
    text-align: center;
    margin-bottom: 28px;
  }

  // ==================== FORM ELEMENTS ====================
  .form-group {
    .custom-field-label {
      display: block;
      font-size: 14px;
      font-weight: 600;
      color: #1E293B;
      margin-bottom: 8px;
      text-align: start;
    }
  }

  .custom-input-field {
    .v-input__slot {
      min-height: 52px !important;
      border-radius: 12px !important;
      background-color: #FFFFFF !important;
      transition: all 0.2s ease !important;

      fieldset {
        border-color: #E2E8F0 !important;
        border-width: 1.5px !important;
        border-radius: 12px !important;
        transition: all 0.2s ease !important;
      }
    }

    &:hover .v-input__slot fieldset {
      border-color: #CBD5E1 !important;
    }

    &.v-input--is-focused .v-input__slot {
      box-shadow: 0 0 0 3.5px rgba(8, 44, 105, 0.1) !important;

      fieldset {
        border-color: #082C69 !important;
        border-width: 2px !important;
      }
    }

    &.error--text .v-input__slot {
      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1) !important;

      fieldset {
        border-color: #DC2626 !important;
      }
    }

    .v-text-field__slot input {
      font-size: 15px !important;
      color: #1E293B !important;
      padding: 12px 0 !important;

      &::placeholder {
        color: #94A3B8 !important;
        opacity: 1 !important;
      }
    }

    .v-input__icon .v-icon {
      color: #64748B !important;
      font-size: 20px !important;
      transition: color 0.2s ease !important;
    }

    &.v-input--is-focused .v-input__icon .v-icon {
      color: #082C69 !important;
    }

    .v-text-field__details {
      padding: 4px 4px 0 !important;
      margin-bottom: 0 !important;

      .v-messages__message {
        color: #DC2626 !important;
        font-size: 12.5px !important;
        font-weight: 500 !important;
        line-height: 1.4 !important;
        text-align: start;
      }
    }
  }

  // ==================== OPTIONS ROW ====================
  .options-row {
    margin-top: 4px;
    margin-bottom: 24px;

    .remember-checkbox {
      .v-label {
        font-size: 14px !important;
        color: #475569 !important;
        font-weight: 500 !important;
      }

      .v-icon {
        color: #94A3B8 !important;
      }

      &.v-input--is-label-active .v-icon {
        color: #082C69 !important;
      }
    }

    .forgot-password-link {
      font-size: 14px;
      font-weight: 600;
      color: #F5A114 !important;
      text-decoration: none !important;
      transition: color 0.2s ease, opacity 0.2s ease;

      &:hover {
        color: #D98A0D !important;
        text-decoration: underline !important;
      }
    }
  }

  // ==================== SUBMIT BUTTON ====================
  .login-submit-btn {
    background: #082C69 !important;
    color: #FFFFFF !important;
    height: 54px !important;
    border-radius: 12px !important;
    letter-spacing: 0 !important;
    text-transform: none !important;
    box-shadow: 0 8px 24px -4px rgba(8, 44, 105, 0.35) !important;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;

    &:hover {
      background: #061B40 !important;
      box-shadow: 0 12px 28px -4px rgba(8, 44, 105, 0.45) !important;
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 4px 12px -2px rgba(8, 44, 105, 0.35) !important;
    }

    .btn-text {
      font-weight: 700;
      font-size: 16px;
      color: #FFFFFF;
    }

    .btn-arrow {
      color: #FFFFFF !important;
    }
  }

  // ==================== AUTH ERROR ALERT ====================
  .auth-error-alert {
    border-radius: 12px !important;
    background-color: #FEF2F2 !important;
    border-color: #FCA5A5 !important;
    color: #B91C1C !important;
    font-size: 13.5px !important;
    font-weight: 500 !important;
  }
}

// ==================== LANGUAGE POPUP MENU ====================
.lang-menu-list {
  background: #FFFFFF !important;
  border-radius: 12px !important;
  box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid #E2E8F0 !important;

  .lang-menu-item {
    border-radius: 8px !important;
    min-height: 38px !important;
    margin: 2px 0;
    cursor: pointer;

    &:hover {
      background: #F8FAFC !important;
    }

    &.active-lang {
      background: rgba(8, 44, 105, 0.08) !important;

      .lang-name {
        color: #082C69 !important;
        font-weight: 700 !important;
      }
    }

    .lang-name {
      font-size: 13.5px !important;
      color: #1E293B !important;
    }
  }
}

// ==================== RESPONSIVE BEHAVIOR ====================
@media (max-width: 1024px) {
  .beepgo-login-wrapper {
    .left-brand-panel {
      flex: 0 0 44%;
      max-width: 44%;
      padding: 40px 36px 0;

      .brand-top-content {
        .brand-headline {
          font-size: 30px;
        }

        .brand-copy {
          font-size: 15.5px;
        }
      }
    }

    .right-login-panel {
      flex: 0 0 56%;
      max-width: 56%;
      padding: 30px 20px;

      .login-card-container {
        max-width: 480px;

        .login-card {
          padding: 36px 32px 36px;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .beepgo-login-wrapper {
    flex-direction: column !important;
    min-height: 100vh;
    overflow-y: auto;

    .left-brand-panel {
      flex: 0 0 auto;
      max-width: 100%;
      width: 100%;
      min-height: auto;
      padding: 32px 24px 16px;
      align-items: center;
      text-align: center;

      .brand-top-content {
        align-items: center;
        text-align: center;

        .brand-logo-container {
          margin-bottom: 20px;

          .beepgo-brand-logo {
            height: 42px;
          }
        }

        .brand-headline {
          font-size: 26px;
          margin-bottom: 8px;
        }

        .brand-copy {
          font-size: 14.5px;
          max-width: 340px;
          margin: 0 auto;
        }
      }

      .brand-transport-scene {
        display: none;
      }
    }

    .right-login-panel {
      flex: 1 0 auto;
      max-width: 100%;
      width: 100%;
      min-height: auto;
      padding: 12px 16px 40px;
      align-items: flex-start;

      .leaves-accent-container {
        display: none;
      }

      .login-card-container {
        max-width: 100%;

        .login-card {
          border-radius: 20px !important;
          padding: 32px 22px 32px;
          box-shadow: 0 12px 32px -8px rgba(8, 44, 105, 0.08) !important;
        }
      }
    }
  }
}
</style>
